
import { getAuth } from "firebase/auth";
import { collection, getDocs, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { BookOpen, Calendar, CheckCircle, Users } from 'lucide-react';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { app } from "../firebase";
import { DatePicker } from '../components/ui/date-picker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import StatCard from '../components/ui/stat-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { storage } from '../lib/storage';
import { Batch, Student } from '../types/common';
import { Switch } from '../components/ui/switch';
import { DataTable } from '../components/ui/data-table';
import Header from "../components/layout/Header";

const Index = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const { toast } = useToast();
  const [courseAttandanceList, setCourseAttendanceList] = useState([]);
  const [courseBatches, setCourseBatches] = useState([]);
  const [courseTodayClasses, setCourseTodayClasses] = useState([]);

  const db = getFirestore(app);
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribeBatches = onSnapshot(
      collection(db, "courseBatches"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs?.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
          .sort(sortByBatchName);
        setBatches(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    const unsubscribeCourseTodayClasses = onSnapshot(
      collection(db, "courseTodayClasses"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCourseTodayClasses(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    const unsubscribeCourseAttendance = onSnapshot(
      collection(db, "courseAttandance"), // Corrected spelling here
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setCourseAttendanceList(result);
      },
      (error) => {
        toast({ title: error.message, description: "" });
      }
    );

    // Proper return function for cleanup
    return () => {
      unsubscribeBatches();
      unsubscribeCourseTodayClasses();
      unsubscribeCourseAttendance();
    };
  }, []);


  const [totalStudents, setTotalStudents] = useState(null);
  const [totalBatches, setTotalBatches] = useState(null);
  const [activeBatches, setactiveBatches] = useState(null);
  const [presentToday, setPresentToday] = useState(null);
  const [batchWisePresentMap, setBatchWisePresentMap] = useState(null);
  const [batchDistributionData, setBatchDistributionData] = useState(null);
  const [presentStudentsData, setStudentsData] = useState([]);
  const [showPresent, setShowPresent] = useState<boolean>(true);
  const [date, setDate] = useState<Date>(new Date());


  const countUniqueStudents = (batches) => {
    const uniqueStudentIds = batches.reduce((acc, batch) => {
      batch?.students?.forEach(studentId => acc.add(studentId)); // Add each student ID to the Set
      return acc; // Return the Set for the next iteration
    }, new Set());

    return uniqueStudentIds.size; // Return the count of unique IDs
  };

  const handleDateChange = (newDate: Date | undefined) => {
    const todayDate = new Date(newDate).toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
    const formattedDate = todayDate.split('/').join('-'); // Convert to DD-MM-YYYY  
    if (formattedDate) {
      setDate(newDate);
      // checkAttendanceAndMarkForStudents(students, courseAttendance, formattedDate);
    }
  };


  const checkTodaysAttendance = (courseAttandance: any, emailIds: any, date: any) => {
    const results = {};

    for (const emailId of emailIds) {
      results[emailId] = false; // Initialize results for each emailId  

      const classInfo = courseAttandance[courseAttandance?.batchName]
      for (const subject of classInfo) {
        //   console.log(subject)
        const conductedSessions = subject[subject?.className].conducted;
        if (conductedSessions[date]) {
          const attendees = conductedSessions[date];
          if (attendees.includes(emailId)) {
            results[emailId] = true; // Update result if emailId is found  
            break; // Exit the loop if found  
          }
        }
      }
    }

    return results; // Return the results object  
  }


  const mapBatchIdToUniqueStudents = (batches) => {
    return batches?.reduce((acc, batch) => {
      // Check if the batch ID is already in the Map
      if (!acc?.has(batch.id)) {
        acc?.set(batch.id, new Set()); // Create a new Set for unique students for this batchId
      }
      // Add each student ID to the Set associated with the batchId
      batch?.students?.forEach(studentId => acc.get(batch.id).add(studentId));
      return acc; // Return the Map for the next iteration
    }, new Map());
  };


  const checkAttendanceAndMarkForStudents = (studentsData, courseAttendanceData, date) => {
    const studentsDataObj = Array.from(studentsData)?.map(x => {
      return { emailId: x };
    });
    const studentEmails = studentsDataObj?.map(x => x.emailId);
    let results;
    if (courseAttendanceData != null) {

      results = checkTodaysAttendance(courseAttendanceData, studentEmails, date);

      // Update each student's attendance based on the results  
      studentsDataObj?.forEach((student: any) => {
        // Check if the student's email exists in the results  
        if (results[student.emailId]) {
          student.attendance = true; // Mark as attended  
        } else {
          student.attendance = false; // Mark as not attended  
        }
      });
    }

    return studentsDataObj;
  };


  const findPresentPeopleOfToday = (date = null) => {
    const batchStudentsMap = mapBatchIdToUniqueStudents(batches);
    // const todayDate = new Date().toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
    // const formattedDate = todayDate.split('/').join('-'); // Convert to YYYY-MM-DD  
    const batchAttandanceCount = new Map();
    const countTotalPresent = { totalStudentsPresentToday: 0 };

    batchStudentsMap.forEach((studentSet, batchId) => {
      const courseAttandance = courseAttandanceList.filter(x => x.batchId == batchId) ?? null;
      const updated = checkAttendanceAndMarkForStudents(studentSet, courseAttandance !== null ? courseAttandance[0] : null, date);
      // batchStudentsMap.set(batchId,updated);
      const count = updated.filter((x: any) => x.attendance)?.length;
      countTotalPresent['totalStudentsPresentToday'] += count;
      batchAttandanceCount.set(batchId, updated);
    });

    return { batchAttandanceCount: batchAttandanceCount, countTotalPresent: countTotalPresent };

  }



  const prepareBatchDistribution = () => {
    // Prepare data for batch distribution chart
    if (batches?.length > 0) {
      const batchDistributionData = batches
        ?.map((batch) => {
          const studentsInBatch = batch?.students?.length;
          return {
            name: batch?.batchName,
            students: studentsInBatch,
            color: getBatchColor(batch?.batchName),
          };
        })
        .sort((a, b) => b.students - a.students)
      // Take only top 6 batches for visualization
      setBatchDistributionData(batchDistributionData);
    }
  }



  const calculateDetails = useMemo(() => {
    if (batches?.length > 0 && courseAttandanceList?.length > 0 && courseTodayClasses?.length > 0) {
      setTotalStudents(countUniqueStudents(batches) as any);

      //

      const todayDate = new Date(date).toLocaleDateString('en-GB'); // Format as DD-MM-YYYY  
      const formattedDate = todayDate.split('/').join('-'); // Convert to DD-MM-YYYY  

      const totalPresentStudentsBatchWiseMap = findPresentPeopleOfToday(formattedDate);
      setTotalBatches(batches.length);
      setactiveBatches(batches.filter(b => b?.isActive)?.length);
      setPresentToday(totalPresentStudentsBatchWiseMap.countTotalPresent?.totalStudentsPresentToday as any);
      prepareBatchDistribution();
      setBatchWisePresentMap(totalPresentStudentsBatchWiseMap);
    }

  }, [batches, courseAttandanceList, courseTodayClasses, date]);



  const fillDataPresentStudentsGrid = useMemo(async () => {
    const allStudentData = [];
    if (batchWisePresentMap !== null) {
      for (const [batchId, studentsSet] of batchWisePresentMap.batchAttandanceCount.entries()) {
        const students = Array.from(studentsSet).filter((x: any) => x.attendance)?.map((student: any) => student.emailId);

        // Create an array of promises for fetching student documents
        const studentPromises = students?.map(async (emailId) => {
          const studentQuery = query(collection(db, "students"), where("emailId", "==", emailId));
          const querySnapshot = await getDocs(studentQuery);

          if (!querySnapshot.empty) {
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          }
          return null;
        });

        const studentData = await Promise.all(studentPromises);
        allStudentData.push(...studentData.filter(Boolean).flat()); // Flatten and filter out nulls
      }
      const allStudentsData = allStudentData.filter((student: any) =>
        student.studentIdNo && student.studentIdNo.includes("T")
      );
      setStudentsData(allStudentsData); // Set the final collected data
    }
  }, [batchWisePresentMap])
  // Prepare data for attendance chart
  const attendanceData = [
    { name: 'Present', value: presentToday, color: '#10B981' },
    { name: 'Absent', value: totalStudents - presentToday, color: '#EF4444' },
  ];

  function sortByBatchName(x: any, y: any) {
    if (x?.batchName > y?.batchName) return 1;
    else if (x?.batchName < y?.batchName) return -1;
    else return 0;
  }

  // Get color for a batch
  function getBatchColor(batchName: string): string {
    if (batchName?.includes('JFS')) return '#3B82F6';
    if (batchName?.includes('PFS')) return '#10B981';
    if (batchName?.includes('DFS')) return '#F59E0B';
    if (batchName?.includes('Testing')) return '#8B5CF6';
    return '#EC4899';
  }

  const getBatchName = (batchId) => {
    return batches?.find(x => x.id == batchId)?.batchName;
  }
  const columns: any = [
    { id: "Id", header: "ID", accessorKey: "studentIdNo" },
    { id: "name", header: "Student Name", accessorKey: "studentsName" },
    {
      id: "batch",
      header: "Batch",
      accessorKey: "batch",
      cell: ({ row }) => {
        const data = row.original;
        let batchId;
        if (data?.batchIds?.length > 0) batchId = data?.batchIds[0];
        return (
          batchId ? (
            <span className={`px-2 py-1 bg-[#06395BFF] text-white rounded-full text-xs ${getBatchName(getBatchColor(data?.batchIds[0])?.split("").filter(x => x !== " ").join(""))}`}>
              {getBatchName(data?.batchIds[0])}
            </span>
          ) : null
        )
      }
    },
    { id: "mobile", header: "Mobile", accessorKey: "mobileNumber" },
    { id: "Email", header: "Email", accessorKey: "emailId" },
  ];


  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Dahsboard" subtitle="Manage students,bacthes,schedules and more" />
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 mt-8 ">
          <div className="col-span-1 text-left">  {/* Changed to text-left */}
            <span className="text-sm font-medium pb-2">Filter Attendance By Date</span> {/* Removed align-left */}
            <DatePicker value={date} onChange={handleDateChange} className="w-full" />
          </div>

          <div className="col-span-1 md:col-span-1 flex flex-col items-center justify-center">
            <div className="text-center">
              <span className="text-bytesplash-blue font-semibold bg-bytesplash-soft-blue rounded-full px-4 py-1 inline-block">
                {format(date, 'MMM d, yyyy')}
              </span>
              <h1 className="text-2xl font-bold text-bytesplash-blue mt-2">Attendance</h1>
            </div>
          </div>


        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-4">
          <StatCard
            icon={<Users className="h-5 w-5 text-blue-500" />}
            label="Total Students"
            value={totalStudents?.toString()}
            iconBg="bg-bytesplash-soft-blue"
          />

          <StatCard
            icon={<Calendar className="h-5 w-5 text-emerald-500" />}
            label="Total Batches"
            value={batches?.length?.toString()}
            iconBg="bg-green-50"
          />

          <StatCard
            icon={<BookOpen className="h-5 w-5 text-amber-500" />}
            label="Active Batches"
            value={activeBatches?.toString()}
            iconBg="bg-amber-50"
          />

          <StatCard
            icon={<CheckCircle className="h-5 w-5 text-purple-500" />}
            label="Present Today"
            value={`${presentToday ?? 0}/${totalStudents ?? 0}`}
            iconBg="bg-purple-50"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium">Batch Distribution</CardTitle>
              <CardDescription>Number of students in each batch</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="bar">
                <TabsList className="mb-4">
                  <TabsTrigger value="bar">Bar Chart</TabsTrigger>
                  <TabsTrigger value="pie">Pie Chart</TabsTrigger>
                </TabsList>

                <TabsContent value="bar" className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={batchDistributionData ?? []}
                      margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => [`${value} students`, 'Enrollment']}
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                      />
                      <Bar
                        dataKey="students"
                        name="Students"
                        radius={[4, 4, 0, 0]}
                      >
                        {batchDistributionData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="pie" className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={batchDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        outerRadius={130}
                        innerRadius={60}
                        paddingAngle={2}
                        dataKey="students"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {batchDistributionData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} students`, 'Students']}
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium">Today's Attendance</CardTitle>
              <CardDescription>Student attendance status for today</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex flex-col items-center mb-4">
                <div className="text-4xl font-bold text-green-600">
                  {totalStudents > 0
                    ? Math.round((presentToday ?? 0) / totalStudents * 100)
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">Attendance Rate</div>
              </div>

              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={presentStudentsData ?? []}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={55}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {presentStudentsData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} students`, 'Count']}
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="flex justify-center space-x-4 mt-2">
                {attendanceData?.map((entry, index) => (
                  <div key={index} className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-1"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-sm font-medium">{entry.name}</span>
                    <span className="text-sm text-gray-500 ml-1">
                      ({Math.round((entry.value / totalStudents) * 100)}%)
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="gap-6 w-full mt-4">

          <Card className="shadow-md">
            <h1 className="text-xl font-bold mb-6 text-bytesplash-blue mt-4">Present Students</h1>
            <CardContent className="p-4 h-full pt-0">
              {
                presentStudentsData && presentStudentsData?.length > 0 ? (
                  <DataTable
                    columns={columns}
                    data={presentStudentsData}
                    noResultsMessage={''}  // Clear message to avoid showing if data exists
                  />
                ) : (
                  <div className="text-center text-gray-500">
                    {`No ${showPresent ? 'present' : 'absent'} students found`}
                  </div>
                )
              }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Index;
